import { useState, useEffect, useMemo } from 'react'
import { Ticket } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadTickets, accelerateTicket } from '../lib/api'
import TicketList from '../components/TicketList'
import TicketsTable from '../components/TicketsTable'
import './DirectorTicketsView.css'

function DirectorTicketsView() {
  const { user } = useAuth()
  const [allTickets, setAllTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'in_progress' | 'completed'>('all')
  const [cardStatusFilter, setCardStatusFilter] = useState<string | null>(null)
  const [buildingFilter, setBuildingFilter] = useState<string>('all')
  const [adminFilter, setAdminFilter] = useState<string>('all')

  useEffect(() => {
    if (user) {
      loadTicketsData()
    }
  }, [user])

  const loadTicketsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      // Директор видит все заявки (как IT администратор)
      const data = await loadTickets(user.id, 'director', undefined)
      setAllTickets(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí')
    } finally {
      setLoading(false)
    }
  }

  // Получаем уникальные здания для фильтра
  const buildings = useMemo(() => {
    const buildingMap = new Map<string, string>()
    allTickets.forEach(ticket => {
      if (ticket.building_id && ticket.building_name) {
        buildingMap.set(ticket.building_id, ticket.building_name)
      }
    })
    return Array.from(buildingMap.entries()).map(([id, name]) => ({ id, name }))
  }, [allTickets])

  // Получаем уникальных администраторов для фильтра
  const administrators = useMemo(() => {
    const adminMap = new Map<string, { name: string, type?: string }>()
    allTickets.forEach(ticket => {
      if (ticket.admin_id && ticket.admin_name) {
        if (!adminMap.has(ticket.admin_id)) {
          adminMap.set(ticket.admin_id, {
            name: ticket.admin_name,
            type: ticket.admin_type
          })
        }
      }
    })
    return Array.from(adminMap.entries()).map(([id, data]) => ({ 
      id, 
      name: data.name,
      type: data.type 
    }))
  }, [allTickets])

  // Фильтрация заявок
  const filteredTickets = useMemo(() => {
    let filtered = [...allTickets]

    // Фильтр по статусу (приоритет у карточки-фильтра)
    const effectiveStatusFilter = cardStatusFilter !== null ? cardStatusFilter : (statusFilter !== 'all' ? statusFilter : null)
    if (effectiveStatusFilter) {
      filtered = filtered.filter(t => t.status === effectiveStatusFilter)
    }

    // Фильтр по зданию
    if (buildingFilter !== 'all') {
      filtered = filtered.filter(t => t.building_id === buildingFilter)
    }

    // Фильтр по администратору
    if (adminFilter !== 'all') {
      filtered = filtered.filter(t => t.admin_id === adminFilter)
    }

    return filtered
  }, [allTickets, cardStatusFilter, statusFilter, buildingFilter, adminFilter])

  const handleAccelerate = async (ticketId: string) => {
    if (!confirm('Opravdu chcete požádat o urychlení této žádosti?')) {
      return
    }

    try {
      const result = await accelerateTicket(ticketId)
      alert(`Žádost o urychlení byla odeslána. Email byl odeslán ${result.emails_sent} ${result.emails_sent === 1 ? 'příjemci' : 'příjemcům'}.`)
    } catch (error: any) {
      console.error('Chyba při žádosti o urychlení:', error)
      alert(error.message || 'Chyba při žádosti o urychlení')
    }
  }

  if (loading) {
    return <div className="director-tickets-view">Načítání...</div>
  }

  return (
    <div className="director-tickets-view">
      <div className="director-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ margin: 0 }}>
          <strong>Ředitel</strong>
        </p>
        <button
          onClick={() => window.location.href = '/public-canteen/statistics'}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
          }}
        >
          📊 Statistiky jídelny
        </button>
      </div>

      <div className="view-controls">
        <div className="view-mode-buttons">
          <button
            className={`view-mode-button ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            📋 Karty
          </button>
          <button
            className={`view-mode-button ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            📊 Tabulka
          </button>
          <button
            onClick={() => window.location.href = '/public-canteen/statistics'}
            className="view-mode-button"
            style={{
              background: '#667eea',
              color: 'white',
              border: '2px solid #667eea',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              marginLeft: '0.5rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#5568d3'
              e.currentTarget.style.borderColor = '#5568d3'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#667eea'
              e.currentTarget.style.borderColor = '#667eea'
            }}
          >
            📊 Statistiky jídelny
          </button>
        </div>

        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'new' | 'in_progress' | 'completed')}
            className="filter-select"
          >
            <option value="all">Všechny statusy</option>
            <option value="new">Nové</option>
            <option value="in_progress">V řešení</option>
            <option value="completed">Dokončené</option>
          </select>

          <select
            value={buildingFilter}
            onChange={(e) => setBuildingFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Všechna budovy</option>
            {buildings.map(building => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>

          <select
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Všichni administrátoři</option>
            {administrators.map(admin => (
              <option key={admin.id} value={admin.id}>
                {admin.name} {admin.type === 'spravce_it' ? '(IT)' : admin.type === 'spravce_budov' ? '(Budovy)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tickets-stats">
        <div 
          className={`stat-card ${cardStatusFilter === 'new' ? 'active' : ''}`}
          onClick={() => {
            setCardStatusFilter(cardStatusFilter === 'new' ? null : 'new')
            setStatusFilter('all')
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-number">{allTickets.filter(t => t.status === 'new').length}</div>
          <div className="stat-label">Nové</div>
        </div>
        <div 
          className={`stat-card ${cardStatusFilter === 'in_progress' ? 'active' : ''}`}
          onClick={() => {
            setCardStatusFilter(cardStatusFilter === 'in_progress' ? null : 'in_progress')
            setStatusFilter('all')
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-number">{allTickets.filter(t => t.status === 'in_progress').length}</div>
          <div className="stat-label">V řešení</div>
        </div>
        <div 
          className={`stat-card ${cardStatusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => {
            setCardStatusFilter(cardStatusFilter === 'completed' ? null : 'completed')
            setStatusFilter('all')
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-number">{allTickets.filter(t => t.status === 'completed').length}</div>
          <div className="stat-label">Dokončené</div>
        </div>
        <div 
          className={`stat-card ${cardStatusFilter === null ? 'active' : ''}`}
          onClick={() => {
            setCardStatusFilter(null)
            setStatusFilter('all')
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-number">{allTickets.length}</div>
          <div className="stat-label">Celkem</div>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <TicketList 
          tickets={filteredTickets} 
          showAssignButton={false}
          showCompleteButton={false}
          showAccelerateButton={true}
          onAccelerate={handleAccelerate}
        />
      ) : (
        <TicketsTable 
          tickets={filteredTickets} 
          showCompleteButton={false}
          showAccelerateButton={true}
          onAccelerate={handleAccelerate}
          filterType="admin"
        />
      )}
    </div>
  )
}

export default DirectorTicketsView

